import React, { useEffect, useRef, useState } from 'react';

/**
 * RuntimeProgressOverlay
 *
 * Global fullscreen overlay for long-running async operations.
 * Uses CSS variables (--surface-1, --text-primary, etc.) so it
 * correctly adapts to both light and dark themes.
 *
 * Props:
 *   progress: {
 *     title: string
 *     statusText: string
 *     message: string
 *     percent: number        — 0–100
 *     error: string|null
 *     onCancel: Function
 *     onRetry: Function
 *     onClose: Function
 *   }
 */
export default function RuntimeProgressOverlay({ progress }) {
  const [animDot, setAnimDot] = useState(0);
  const dotTimer = useRef(null);

  useEffect(() => {
    if (!progress || progress.error) {
      clearInterval(dotTimer.current);
      return;
    }
    dotTimer.current = setInterval(() => setAnimDot(d => (d + 1) % 4), 500);
    return () => clearInterval(dotTimer.current);
  }, [progress]);

  if (!progress) return null;

  const { title, statusText, message, percent, error, onCancel, onRetry, onClose } = progress;
  const dots = '.'.repeat(animDot);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9998,
      background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', sans-serif",
      padding: 24,
    }}>
      <div style={{
        background: 'var(--surface-1, #18181b)',
        border: '1px solid var(--card-border, rgba(255,255,255,0.08))',
        borderRadius: 20,
        padding: '40px 48px',
        width: '100%',
        maxWidth: 440,
        textAlign: 'center',
        boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
      }}>

        {/* Icon / Spinner */}
        {error ? (
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(239,68,68,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width={28} height={28} fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
        ) : (
          <div style={{ position: 'relative', width: 56, height: 56 }}>
            <svg width={56} height={56} viewBox="0 0 56 56" style={{ position: 'absolute', inset: 0, animation: 'rpo-spin 1.1s linear infinite' }}>
              <circle cx={28} cy={28} r={24} fill="none" stroke="rgba(252,163,17,0.15)" strokeWidth={4} />
              <circle cx={28} cy={28} r={24} fill="none" stroke="#FCA311" strokeWidth={4} strokeLinecap="round" strokeDasharray="40 110" strokeDashoffset={0} />
            </svg>
            <div style={{
              position: 'absolute', inset: 12,
              borderRadius: '50%',
              background: 'rgba(252,163,17,0.12)',
              animation: 'rpo-pulse 1.8s ease-in-out infinite',
            }} />
          </div>
        )}

        {/* Title */}
        <div style={{
          fontSize: 18,
          fontWeight: 700,
          color: 'var(--text-primary, #f1f5f9)',
          lineHeight: 1.3,
        }}>
          {title || (error ? 'Something went wrong' : 'Working...')}
        </div>

        {/* Status Text */}
        {statusText && !error && (
          <div style={{
            fontSize: 13.5,
            color: 'var(--text-secondary, #94a3b8)',
            fontWeight: 500,
          }}>
            {statusText}{dots}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.2)',
            fontSize: 12.5,
            color: '#fca5a5',
            lineHeight: 1.6,
            textAlign: 'left',
            width: '100%',
          }}>
            {error}
          </div>
        )}

        {/* Optional longer message */}
        {message && (
          <div style={{
            fontSize: 12,
            color: 'var(--text-muted, #64748b)',
            lineHeight: 1.7,
          }}>
            {message}
          </div>
        )}

        {/* Progress Bar */}
        {typeof percent === 'number' && !error && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{
              width: '100%', height: 5, borderRadius: 5,
              background: 'var(--surface-3, rgba(255,255,255,0.07))',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${Math.max(2, Math.min(100, percent))}%`,
                background: 'linear-gradient(90deg, #14213D, #FCA311)',
                borderRadius: 5,
                transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              }} />
            </div>
            <div style={{
              fontSize: 11,
              color: 'var(--text-muted, #475569)',
              textAlign: 'right',
            }}>
              {Math.round(percent)}%
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 4, width: '100%' }}>
          {error && onRetry && (
            <button
              onClick={onRetry}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                background: 'linear-gradient(135deg, #14213D, #FCA311)',
                border: 'none', color: '#fff', fontSize: 13,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              ↺ Retry
            </button>
          )}
          {error && onClose && (
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                background: 'var(--surface-2, rgba(255,255,255,0.06))',
                border: '1px solid var(--card-border, rgba(255,255,255,0.1))',
                color: 'var(--text-secondary, #94a3b8)',
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
              }}
            >
              Close
            </button>
          )}
          {!error && onCancel && (
            <button
              onClick={onCancel}
              style={{
                padding: '9px 24px', borderRadius: 10,
                background: 'var(--surface-2, rgba(255,255,255,0.04))',
                border: '1px solid var(--card-border, rgba(255,255,255,0.08))',
                color: 'var(--text-muted, #64748b)',
                fontSize: 12.5, fontWeight: 500,
                cursor: 'pointer', margin: '0 auto',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes rpo-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes rpo-pulse {
          0%, 100% { opacity: 0.4; transform: scale(0.9); }
          50%       { opacity: 0.8; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}
